import React, { useState } from 'react';
import { orders as ordersApi } from '../utils/api';
import { fmtBRL, fmtUSD, fmtRMB, fmtDate } from '../utils/helpers';
import { useToast, ToastContainer } from '../hooks/useToast';
import { CAT_LABELS, fmtIn } from '../utils/payments';
import PaymentForm from './PaymentForm';
import FinancialDocs, { DOC_TYPES } from './FinancialDocs';

// Leitura de valores digitados — reexportada para quem já importava daqui
export { parseBRL, parseRate } from '../utils/payments';

export default function PaymentsTab({ quotation: q, onRefresh, toast: pageToast }) {
  const payments = q.payments || [];
  const balance = q.balance || null;
  // As mensagens aparecem no ToastContainer da página; sem ele (uso avulso),
  // a aba mostra as suas — antes o toast local nunca era exibido.
  const local = useToast();
  const toast = pageToast || local.toast;
  const [showForm, setShowForm] = useState(false);
  // Pagamento em edição (null = estamos cadastrando um novo)
  const [editing, setEditing] = useState(null);
  const rows = balance?.rows || [];

  function openNew() { setEditing(null); setShowForm(true); }
  function startEdit(p) { setEditing(p); setShowForm(true); }

  async function handleDelete(pid) {
    if (!window.confirm('Excluir pagamento?')) return;
    try { await ordersApi.deletePayment(pid); onRefresh(); }
    catch (e) { toast(e.response?.data?.error || 'Erro', 'error'); }
  }

  // chips das categorias de um pagamento (alocações ou categoria legada)
  function paymentCats(p) {
    if (Array.isArray(p.allocations) && p.allocations.length) return p.allocations;
    return [{ category: p.category || 'fornecedor', amount_brl: p.amount_brl, is_final: 0 }];
  }

  return (
    <div style={{ padding: 24 }}>
      {/* Saldo por categoria */}
      {balance && (
        <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: 20 }}>
          <div style={{ padding: '10px 16px', fontWeight: 700, fontSize: 13, color: 'var(--pantone)', borderBottom: '1px solid var(--gray-100)' }}>
            💰 Saldo por categoria
          </div>
          <table className="table">
            <thead>
              <tr>
                <th>Categoria</th>
                <th style={{ textAlign: 'right' }}>Devido (previsto)</th>
                <th style={{ textAlign: 'right' }}>Pago</th>
                <th style={{ textAlign: 'right' }}>Saldo</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const fx = r.balance_foreign != null;
                // Fornecedor/frete: moeda do pedido em destaque; o BRL embaixo é
                // o previsto (devido/saldo) ou o efetivamente desembolsado (pago).
                const cell = (brl, foreign, hint) => fx
                  ? <>{fmtIn(r.currency, foreign)}<div style={{ fontSize: 11, fontWeight: 400, color: 'var(--gray-400)' }}>{fmtBRL(brl)} {hint}</div></>
                  : fmtBRL(brl);
                return (
                  <tr key={r.category} style={{ background: r.settled ? '#F0FFF4' : 'transparent' }}>
                    <td>
                      {CAT_LABELS[r.category] || r.category}
                      {r.final && <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: '#276749', background: '#C6F6D5', padding: '1px 6px', borderRadius: 8 }}>final</span>}
                    </td>
                    <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)' }}>{cell(r.due, r.due_foreign, 'previsto')}</td>
                    <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', color: 'var(--pantone)' }}>{cell(r.paid, r.paid_foreign, 'desembolsado')}</td>
                    <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', fontWeight: 700, color: r.settled ? '#276749' : '#C53030' }}>{cell(r.balance, r.balance_foreign, 'previsto')}</td>
                  </tr>
                );
              })}
              <tr style={{ background: 'var(--accent-light, #FAF5F6)', fontWeight: 700 }}>
                <td>TOTAL</td>
                <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)' }}>{fmtBRL(balance.totalDue)}</td>
                <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', color: 'var(--pantone)' }}>{fmtBRL(balance.totalPaid)}</td>
                <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', color: balance.totalBalance > 0.01 ? '#C53030' : '#276749' }}>{fmtBRL(balance.totalBalance)}</td>
              </tr>
            </tbody>
          </table>
          <div style={{ padding: '6px 16px', fontSize: 11, color: 'var(--gray-400)', fontStyle: 'italic' }}>
            Devido = custo efetivo por categoria (frete rateado do container incluído). Impostos = tributos brutos (antes de recuperáveis). "final" = categoria quitada (o realizado prevalece sobre o previsto).
            {' '}Fornecedor e frete são dívidas fixas na moeda negociada: o saldo abate o valor pago nessa moeda, qualquer que seja o câmbio. O BRL do saldo é só previsão (restante × câmbio travado); o BRL pago é o que efetivamente saiu do caixa.
          </div>
        </div>
      )}

      {balance?.closing && balance.totalPaid > 0 && (() => {
        const c = balance.closing;
        const cur = balance.currency || 'USD';
        const box = (label, value, sub) => (
          <div style={{ flex: '1 1 170px', padding: '10px 14px', background: 'var(--gray-50)', borderRadius: 'var(--radius)', border: '1px solid var(--gray-200)' }}>
            <div style={{ fontSize: 11, color: 'var(--gray-500)', fontWeight: 600, textTransform: 'uppercase' }}>{label}</div>
            <div style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 16, color: 'var(--pantone)' }}>{value}</div>
            {sub && <div style={{ fontSize: 11, color: 'var(--gray-400)' }}>{sub}</div>}
          </div>
        );
        return (
          <div className="card" style={{ padding: 16, marginBottom: 20 }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--pantone)', marginBottom: 10 }}>
              {c.settled ? '✅ Fechamento do pedido — quitado' : '🧾 Gasto até agora'}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              {box('Total desembolsado (R$)', fmtBRL(c.spent_brl), 'soma real de todos os pagamentos')}
              {box(`Fornecedor + frete (${cur})`, fmtIn(cur, c.spent_foreign), `custou ${fmtBRL(c.spent_foreign_brl)}`)}
              {box('Câmbio médio efetivo', c.avg_rate ? `R$ ${c.avg_rate.toFixed(4)}` : '—', `custos em reais: ${fmtBRL(c.spent_local_brl)}`)}
              {box(`Custo total em ${cur}`, c.spent_total_foreign != null ? fmtIn(cur, c.spent_total_foreign) : '—', 'total em R$ ÷ câmbio médio')}
              {!c.settled && box('Custo total previsto (R$)', fmtBRL(c.projected_brl), 'desembolsado + saldo previsto')}
            </div>
          </div>
        );
      })()}

      {/* Histórico de pagamentos */}
      <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: 16 }}>
        <table className="table">
          <thead>
            <tr>
              <th>Data</th>
              <th>Categorias</th>
              <th>Valor</th>
              <th>Câmbio</th>
              <th>Valor (BRL)</th>
              <th>Comprovante</th>
              <th>Obs.</th>
              <th style={{ width: 90 }}></th>
            </tr>
          </thead>
          <tbody>
            {payments.length === 0 && (
              <tr><td colSpan={8} style={{ textAlign: 'center', color: 'var(--gray-400)', padding: 24 }}>Nenhum pagamento registrado</td></tr>
            )}
            {payments.map(p => (
              <tr key={p.id}>
                <td>{fmtDate(p.payment_date)}</td>
                <td>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {paymentCats(p).map((a, i) => (
                      <span key={i} title={fmtBRL(a.amount_brl)} style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: a.is_final ? '#C6F6D5' : 'var(--gray-100)', color: a.is_final ? '#276749' : 'var(--gray-600)' }}>
                        {CAT_LABELS[a.category] || a.category}{a.is_final ? ' ✓' : ''}
                      </span>
                    ))}
                  </div>
                </td>
                <td style={{ fontFamily: 'var(--font-mono)' }}>{p.currency === 'RMB' ? fmtRMB(p.amount_foreign) : p.currency === 'BRL' ? fmtBRL(p.amount_foreign) : fmtUSD(p.amount_foreign)}</td>
                <td style={{ fontFamily: 'var(--font-mono)' }}>
                  {(() => {
                    // Pago em reais com equivalente em moeda: câmbio = BRL ÷ moeda quitada
                    const eq = paymentCats(p).filter(a => a.amount_order > 0);
                    const ord = eq.reduce((s, a) => s + a.amount_order, 0);
                    const brl = eq.reduce((s, a) => s + (a.amount_brl || 0), 0);
                    if (p.currency === 'BRL' && ord > 0) {
                      return <>R$ {(brl / ord).toFixed(4)}<div style={{ fontSize: 10, color: 'var(--gray-400)' }}>quitou {fmtIn(balance?.currency || 'USD', ord)}</div></>;
                    }
                    return `R$ ${parseFloat(p.exchange_rate).toFixed(4)}`;
                  })()}
                </td>
                <td style={{ fontWeight: 700, color: 'var(--pantone)', fontFamily: 'var(--font-mono)' }}>{fmtBRL(p.amount_brl)}</td>
                <td>
                  {p.receipt_filename
                    ? <a href={`/uploads/${p.receipt_filename}`} target="_blank" rel="noreferrer" style={{ color: 'var(--blue)', fontSize: 12 }}>📎 {p.receipt_original_name || 'Comprovante'}</a>
                    : <span style={{ color: 'var(--gray-300)', fontSize: 12 }}>—</span>}
                  {/* invoices/numerários quitados por este pagamento */}
                  {(q.financial_documents || []).filter(d => d.payment_id === p.id).map(d => (
                    <div key={d.id}>
                      <a href={`/uploads/${d.filename}`} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: DOC_TYPES[d.doc_type]?.color || 'var(--gray-600)' }}
                        title={d.original_name}>
                        {DOC_TYPES[d.doc_type]?.icon} {DOC_TYPES[d.doc_type]?.label}{d.doc_number ? ` ${d.doc_number}` : ''}
                      </a>
                    </div>
                  ))}
                </td>
                <td style={{ fontSize: 12, color: 'var(--gray-500)' }}>{p.payee ? `${p.payee} · ` : ''}{p.notes || '—'}</td>
                <td>
                  {p.group_payment_id ? (
                    <a href={q.consolidation ? `/consolidations/${q.consolidation.id}` : '#'} title="Parte de um pagamento consolidado — edite ou exclua pela consolidação"
                      style={{ fontSize: 11, fontWeight: 700, color: '#075985', whiteSpace: 'nowrap' }}>🔗 {q.consolidation?.code || 'consolidado'}</a>
                  ) : (
                  <div style={{ display: 'flex', gap: 2 }}>
                    <button className="btn btn-ghost btn-sm btn-icon" title="Editar pagamento" onClick={() => startEdit(p)}>✏️</button>
                    <button className="btn btn-ghost btn-sm btn-icon" style={{ color: 'var(--error)' }} title="Excluir pagamento" onClick={() => handleDelete(p.id)}>🗑️</button>
                  </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <button className="btn btn-primary btn-sm" onClick={openNew}>
        + Registrar Pagamento
      </button>

      <FinancialDocs quotation={q} onRefresh={onRefresh} toast={toast} />

      {showForm && (
        <PaymentForm
          key={editing ? editing.id : 'new'}
          quotation={q}
          editing={editing}
          toast={toast}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); setEditing(null); onRefresh(); }}
        />
      )}
      {!pageToast && <ToastContainer toasts={local.toasts} />}
    </div>
  );
}
